import React, { useRef, useState, useCallback, useLayoutEffect } from "react";
import styles from "./HorizontalScrollContainer.module.scss";

/**
 * 좌우 스크롤 가능한 컨테이너 + 양쪽 chevron 스크롤 버튼
 *
 * 피그마: 칩바, 전체필터 탭 등 수평 스크롤이 필요한 곳에 공통 사용
 * - 좌측으로 스크롤된 상태면 좌측 chevron 표시
 * - 우측으로 더 스크롤할 수 있으면 우측 chevron 표시
 */
const HorizontalScrollContainer = ({
  children,
  className,
  scrollAmount = 120,
  paddingLeft = 20,
  rightPadding = 48,
}) => {
  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const checkScrollable = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeftArrow(el.scrollLeft > 1);
    setShowRightArrow(el.scrollWidth > el.clientWidth + el.scrollLeft + 1);
  }, []);

  useLayoutEffect(() => {
    checkScrollable();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", checkScrollable, { passive: true });
    window.addEventListener("resize", checkScrollable);
    return () => {
      el.removeEventListener("scroll", checkScrollable);
      window.removeEventListener("resize", checkScrollable);
    };
  }, [checkScrollable]);

  // children 변경 시 스크롤 상태 재확인
  useLayoutEffect(() => {
    checkScrollable();
  }, [children, checkScrollable]);

  return (
    <div
      className={`${styles.container}${className ? ` ${className}` : ""}`}
      style={{ paddingLeft, paddingRight: 20 }}
    >
      {showLeftArrow && (
        <div className={styles.scrollHintLeft}>
          <button
            type="button"
            className={styles.scrollArrowBtn}
            onClick={() => {
              const el = scrollRef.current;
              if (!el) return;
              // 남은 스크롤이 scrollAmount 이하면 맨 앞으로
              const target = el.scrollLeft <= scrollAmount ? 0 : el.scrollLeft - scrollAmount;
              el.scrollTo({ left: target, behavior: "smooth" });
            }}
          >
            <ChevronLeftIcon />
          </button>
        </div>
      )}

      <div className={styles.scrollArea} ref={scrollRef}>
        <div className={styles.inner} style={{ paddingRight: rightPadding }}>
          {children}
        </div>
      </div>

      {showRightArrow && (
        <div className={styles.scrollHintRight}>
          <button
            type="button"
            className={styles.scrollArrowBtn}
            onClick={() => {
              const el = scrollRef.current;
              if (!el) return;
              const maxScroll = el.scrollWidth - el.clientWidth;
              const remaining = maxScroll - el.scrollLeft;
              const target = remaining <= scrollAmount ? maxScroll : el.scrollLeft + scrollAmount;
              el.scrollTo({ left: target, behavior: "smooth" });
            }}
          >
            <ChevronRightIcon />
          </button>
        </div>
      )}
    </div>
  );
};

const ChevronLeftIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11.25 4.5L6.75 9L11.25 13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6.75 4.5L11.25 9L6.75 13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default HorizontalScrollContainer;
